import * as React from 'react'

import {
  loadActiveAgentConversation,
  openTab,
  useAppDispatch,
  useAppStore
} from '@axilon-apps/domains-ontology-ui-state'

import { OrchestratorPane } from '../Chat/OrchestratorPane'
import { useOrchestratorChatController } from '../Chat/useOrchestratorChatController'
import {
  clearIngestQueryConversationId,
  readIngestQueryConversationId,
  writeIngestQueryConversationId
} from './ingestQueryConversationStorage'

const ASK_EMPTY_PROMPTS = [
  'What has the lead agent done so far?',
  'Why was this equipment added to the graph?',
  'What property conflicts are still open?'
]

/**
 * Ask tab for Current Ingest — job-scoped `ingest_query_agent` chat with
 * graph-write propose cards. Conversation id is persisted per job so the
 * lane survives pane remounts; CoT / direct messaging stay on the Agent tab.
 */
export function IngestAskLane({ jobId }: { jobId: string }) {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const [conversationId, setConversationId] = React.useState<string | null>(() =>
    readIngestQueryConversationId(jobId)
  )
  const hydrateGenRef = React.useRef(0)

  const rememberConversation = React.useCallback(
    (id: string) => {
      writeIngestQueryConversationId(jobId, id)
      setConversationId(id)
    },
    [jobId]
  )

  const chat = useOrchestratorChatController({
    mode: 'ingest_query_agent_only',
    graphWrite: true,
    ingestQueryJobId: jobId,
    scopedConversationId: conversationId,
    onScopedConversationCreated: rememberConversation
  })

  // Restore the job's Ask conversation without changing the global Chat tab.
  // Generation guard: a jobId switch / unmount ignores the previous load's
  // settlement so it cannot clear the new job's conversation id. Do not seed
  // `turns: []` — that wipes a cached transcript and flashes empty.
  React.useEffect(() => {
    const gen = ++hydrateGenRef.current
    const stored = readIngestQueryConversationId(jobId)
    setConversationId(stored)
    if (!stored) return

    dispatch(openTab({ id: stored, hidden: true }))
    if (store.getState().agentOrchestrator.tabs[stored]?.turnsLoaded) return

    void dispatch(loadActiveAgentConversation(stored, { hidden: true })).catch(() => {
      if (hydrateGenRef.current !== gen) return
      clearIngestQueryConversationId(jobId)
      setConversationId(null)
    })
  }, [jobId, dispatch, store])

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <OrchestratorPane
        chat={chat}
        emptyStatePrompts={ASK_EMPTY_PROMPTS}
        composerPlaceholder="Ask anything about this ingest..."
        layout="rail"
        showLanding={false}
      />
    </div>
  )
}
